import { useEffect, useRef, useState } from "react";
import { useDebounce } from "usehooks-ts";

import { useAddressPredictionsType, AutoCompleteType } from "./types";
import { getCurrentLocale, getCurrentRegion } from "../../utils";

export function useAddressPredictions({
  region,
  locale,
}: useAddressPredictionsType) {
  const [value, setValue] = useState("");
  const [predictions, setPredictions] = useState([]);
  const debouncedValue = useDebounce<string>(value, 500);
  const autocomplete = useRef<AutoCompleteType>();

  // initialize Autocomplete once window.google detetcted
  useEffect(() => {
    if (!autocomplete.current && window.google) {
      try {
        autocomplete.current =
          new window.google.maps.places.AutocompleteService();
      } catch (e) {
        console.error("Cannot connect to Google Places API", e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.google]);

  useEffect(() => {
    const request = {
      input: value,
      region: region ?? getCurrentRegion(),
      language: locale ?? getCurrentLocale(),
    };

    try {
      autocomplete.current?.getPlacePredictions(request, (predictions: any) => {
        setPredictions(predictions);
      });
    } catch (e) {
      console.error("Cannot retrieve Google Predictions", e);
    }
  }, [debouncedValue, locale, region, value]);

  const getPlacePredictions = (input: string) => {
    if (!input) setPredictions([]);
    setValue(input);
  };

  const getDetails = async (placeId: string) => {
    if (!autocomplete.current) return null;

    // we're not using a map, this element is required to fetch place details
    const map = new google.maps.Map(document.createElement("div"));
    const googlePlaces = new google.maps.places.PlacesService(map);

    return new Promise(function (resolve, _) {
      googlePlaces.getDetails({ placeId }, (place, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK)
          resolve({
            addressComponents: place?.address_components,
            address: place?.formatted_address,
          });
      });
    });
  };

  return {
    getPlacePredictions,
    predictions,
    getDetails,
  };
}
